import type {
  AgentWorkSurfaceObjectType,
  AgentWorkbenchStatus,
  BriefState,
} from "../../../shared/types";

export type WorkObjectCategory =
  | "canonical_brand_knowledge"
  | "working_content"
  | "research_knowledge"
  | "reusable_memory"
  | "review_object"
  | "machine_record";

export type WorkObjectPrimitive =
  | "document_workspace"
  | "revision_workspace"
  | "review_sheet"
  | "card_detail_workspace"
  | "system_trace_view";

export type WorkObjectTab =
  | "document"
  | "revision"
  | "review"
  | "history"
  | "evidence"
  | "runtime"
  | "trace"
  | "entry"
  | "links"
  | "source"
  | "summary";

export type WorkObjectType =
  | AgentWorkSurfaceObjectType
  | "brief"
  | "voice_review"
  | "brand_context"
  | "surface_context"
  | "published_handoff"
  | "market_research_report"
  | "audit_report"
  | "persona_research"
  | "positioning_doc"
  | "corpus_entry"
  | "clarity_review"
  | "approval_rationale"
  | "finding"
  | "unknown";

export type WorkObjectDisplayState =
  | AgentWorkbenchStatus
  | BriefState
  | "clean"
  | "revision_pending"
  | "needs_review"
  | "applied"
  | "open"
  | "passed"
  | "failed"
  | "skipped"
  | "accepted_with_edits"
  | "generated"
  | "archived"
  | "unknown";

export interface WorkObjectPrimaryCta {
  label: string;
  action: "edit" | "open_revision" | "accept_revision" | "approve" | "return_for_edits" | "save" | "open" | "retry" | "inspect";
}

export interface WorkObjectDisplaySpec {
  objectType: WorkObjectType;
  category: WorkObjectCategory;
  primitive: WorkObjectPrimitive;
  defaultTab: WorkObjectTab;
  typeLabel: string;
  state: WorkObjectDisplayState;
  primaryCta: WorkObjectPrimaryCta;
  secondarySurfaces: WorkObjectTab[];
  isHumanFacing: boolean;
  targetRequired: boolean;
}

interface BaseSpec {
  category: WorkObjectCategory;
  primitive: WorkObjectPrimitive;
  defaultTab: WorkObjectTab;
  typeLabel: string;
  secondarySurfaces: WorkObjectTab[];
  isHumanFacing: boolean;
  targetRequired?: boolean;
}

type CtaRule = {
  objectType: WorkObjectType;
  state: WorkObjectDisplayState | "*";
  cta: WorkObjectPrimaryCta;
};

const BASE_SPECS: Record<WorkObjectType, BaseSpec> = {
  brand_voice: {
    category: "canonical_brand_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Canonical",
    secondarySurfaces: ["revision", "history", "runtime"],
    isHumanFacing: true,
  },
  strategy_artifact: {
    category: "canonical_brand_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Canonical",
    secondarySurfaces: ["revision", "history", "runtime"],
    isHumanFacing: true,
  },
  brand_context: {
    category: "canonical_brand_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Canonical",
    secondarySurfaces: ["history", "runtime"],
    isHumanFacing: true,
  },
  surface_context: {
    category: "canonical_brand_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Canonical",
    secondarySurfaces: ["history", "runtime"],
    isHumanFacing: true,
  },
  brief: {
    category: "working_content",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Brief",
    secondarySurfaces: ["review", "trace", "history"],
    isHumanFacing: true,
  },
  campaign_brief: {
    category: "working_content",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Brief",
    secondarySurfaces: ["review", "trace", "history"],
    isHumanFacing: true,
  },
  task_run_brief: {
    category: "working_content",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Brief",
    secondarySurfaces: ["review", "trace", "history"],
    isHumanFacing: true,
  },
  daily_brief: {
    category: "working_content",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Brief",
    secondarySurfaces: ["source", "trace"],
    isHumanFacing: true,
  },
  draft: {
    category: "working_content",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Draft",
    secondarySurfaces: ["review", "trace", "history"],
    isHumanFacing: true,
  },
  published_handoff: {
    category: "working_content",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Published",
    secondarySurfaces: ["source", "trace", "history"],
    isHumanFacing: true,
  },
  market_research_report: {
    category: "research_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Research",
    secondarySurfaces: ["source", "trace", "history"],
    isHumanFacing: true,
  },
  audit_report: {
    category: "research_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Research",
    secondarySurfaces: ["source", "trace", "history"],
    isHumanFacing: true,
  },
  persona_research: {
    category: "research_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Research",
    secondarySurfaces: ["source", "history"],
    isHumanFacing: true,
  },
  positioning_doc: {
    category: "research_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Research",
    secondarySurfaces: ["source", "history"],
    isHumanFacing: true,
  },
  research_artifact: {
    category: "research_knowledge",
    primitive: "document_workspace",
    defaultTab: "document",
    typeLabel: "Research",
    secondarySurfaces: ["source", "trace", "history"],
    isHumanFacing: true,
  },
  corpus_entry: {
    category: "reusable_memory",
    primitive: "card_detail_workspace",
    defaultTab: "entry",
    typeLabel: "Memory",
    secondarySurfaces: ["source", "links"],
    isHumanFacing: true,
  },
  voice_review: {
    category: "review_object",
    primitive: "review_sheet",
    defaultTab: "review",
    typeLabel: "Review",
    secondarySurfaces: ["document", "evidence", "history"],
    isHumanFacing: true,
    targetRequired: true,
  },
  clarity_review: {
    category: "review_object",
    primitive: "review_sheet",
    defaultTab: "review",
    typeLabel: "Review",
    secondarySurfaces: ["document", "evidence", "history"],
    isHumanFacing: true,
    targetRequired: true,
  },
  approval_rationale: {
    category: "review_object",
    primitive: "review_sheet",
    defaultTab: "review",
    typeLabel: "Review",
    secondarySurfaces: ["document", "history"],
    isHumanFacing: true,
    targetRequired: true,
  },
  task_run: {
    category: "machine_record",
    primitive: "system_trace_view",
    defaultTab: "summary",
    typeLabel: "System",
    secondarySurfaces: ["trace", "source"],
    isHumanFacing: false,
  },
  finding: {
    category: "machine_record",
    primitive: "system_trace_view",
    defaultTab: "summary",
    typeLabel: "System",
    secondarySurfaces: ["source", "trace"],
    isHumanFacing: false,
  },
  unknown: {
    category: "machine_record",
    primitive: "system_trace_view",
    defaultTab: "summary",
    typeLabel: "System",
    secondarySurfaces: ["trace"],
    isHumanFacing: false,
  },
};

const WORK_OBJECT_TYPES = Object.keys(BASE_SPECS) as WorkObjectType[];

export const WORK_OBJECT_CTA_RULES: readonly CtaRule[] = [
  { objectType: "brand_voice", state: "revision_pending", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "brand_voice", state: "needs_review", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "brand_voice", state: "draft", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "brand_voice", state: "in_review", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "brand_voice", state: "*", cta: { label: "Edit document", action: "edit" } },

  { objectType: "strategy_artifact", state: "revision_pending", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "strategy_artifact", state: "needs_review", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "strategy_artifact", state: "draft", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "strategy_artifact", state: "in_review", cta: { label: "Open revision draft", action: "open_revision" } },
  { objectType: "strategy_artifact", state: "*", cta: { label: "Edit document", action: "edit" } },

  { objectType: "brand_context", state: "*", cta: { label: "Edit document", action: "edit" } },
  { objectType: "surface_context", state: "*", cta: { label: "Edit document", action: "edit" } },

  { objectType: "brief", state: "blocked", cta: { label: "Inspect issue", action: "inspect" } },
  { objectType: "brief", state: "*", cta: { label: "Open document", action: "open" } },
  { objectType: "campaign_brief", state: "blocked", cta: { label: "Inspect issue", action: "inspect" } },
  { objectType: "campaign_brief", state: "*", cta: { label: "Open document", action: "open" } },
  { objectType: "task_run_brief", state: "blocked", cta: { label: "Inspect issue", action: "inspect" } },
  { objectType: "task_run_brief", state: "*", cta: { label: "Open document", action: "open" } },
  { objectType: "daily_brief", state: "*", cta: { label: "Open document", action: "open" } },
  { objectType: "draft", state: "blocked", cta: { label: "Inspect issue", action: "inspect" } },
  { objectType: "draft", state: "*", cta: { label: "Approve copy", action: "approve" } },
  { objectType: "published_handoff", state: "*", cta: { label: "Open handoff", action: "open" } },

  { objectType: "market_research_report", state: "*", cta: { label: "Save report to Corpus", action: "save" } },
  { objectType: "research_artifact", state: "*", cta: { label: "Save report to Corpus", action: "save" } },
  { objectType: "persona_research", state: "*", cta: { label: "Use in strategy", action: "open" } },
  { objectType: "audit_report", state: "*", cta: { label: "Open document", action: "open" } },
  { objectType: "positioning_doc", state: "*", cta: { label: "Open document", action: "open" } },

  { objectType: "corpus_entry", state: "*", cta: { label: "Save changes", action: "save" } },

  { objectType: "voice_review", state: "failed", cta: { label: "Return for edits", action: "return_for_edits" } },
  { objectType: "voice_review", state: "passed", cta: { label: "Approve target", action: "approve" } },
  { objectType: "voice_review", state: "accepted_with_edits", cta: { label: "Approve target", action: "approve" } },
  { objectType: "voice_review", state: "*", cta: { label: "Apply suggested correction", action: "accept_revision" } },
  { objectType: "clarity_review", state: "failed", cta: { label: "Return for edits", action: "return_for_edits" } },
  { objectType: "clarity_review", state: "*", cta: { label: "Approve target", action: "approve" } },
  { objectType: "approval_rationale", state: "*", cta: { label: "Approve target", action: "approve" } },

  { objectType: "task_run", state: "failed", cta: { label: "Retry Run", action: "retry" } },
  { objectType: "task_run", state: "skipped", cta: { label: "Rerun Action", action: "retry" } },
  { objectType: "task_run", state: "*", cta: { label: "Retry Run", action: "retry" } },
  { objectType: "finding", state: "*", cta: { label: "Open source run", action: "open" } },
  { objectType: "unknown", state: "*", cta: { label: "Open source run", action: "open" } },
] as const;

function isKnownWorkObjectType(value: string): value is WorkObjectType {
  return WORK_OBJECT_TYPES.includes(value as WorkObjectType);
}

function resolvePrimaryCta(
  objectType: WorkObjectType,
  state: WorkObjectDisplayState,
): WorkObjectPrimaryCta {
  const exact = WORK_OBJECT_CTA_RULES.find((rule) => rule.objectType === objectType && rule.state === state);
  if (exact) return exact.cta;
  const fallback = WORK_OBJECT_CTA_RULES.find((rule) => rule.objectType === objectType && rule.state === "*");
  if (fallback) return fallback.cta;
  return { label: "Open source run", action: "open" };
}

export function normalizeWorkObjectType(value: string | null | undefined): WorkObjectType {
  if (!value?.trim()) return "unknown";
  return isKnownWorkObjectType(value) ? value : "unknown";
}

export function workObjectDisplaySpec(
  objectTypeInput: string | null | undefined,
  state: WorkObjectDisplayState = "unknown",
): WorkObjectDisplaySpec {
  const objectType = normalizeWorkObjectType(objectTypeInput);
  const base = BASE_SPECS[objectType];

  return {
    objectType,
    category: base.category,
    primitive: base.primitive,
    defaultTab: base.defaultTab,
    typeLabel: base.typeLabel,
    state,
    primaryCta: resolvePrimaryCta(objectType, state),
    secondarySurfaces: base.secondarySurfaces,
    isHumanFacing: base.isHumanFacing,
    targetRequired: Boolean(base.targetRequired),
  };
}

export function workObjectCategory(objectTypeInput: string | null | undefined): WorkObjectCategory {
  return workObjectDisplaySpec(objectTypeInput).category;
}

export function workObjectDefaultTab(objectTypeInput: string | null | undefined): WorkObjectTab {
  return workObjectDisplaySpec(objectTypeInput).defaultTab;
}

export function workObjectPrimitive(
  objectTypeInput: string | null | undefined,
  state: WorkObjectDisplayState = "unknown",
): WorkObjectPrimitive {
  const spec = workObjectDisplaySpec(objectTypeInput, state);
  if (spec.primitive === "document_workspace" && state === "revision_pending") {
    return "revision_workspace";
  }
  return spec.primitive;
}

export function workObjectRequiresTarget(objectTypeInput: string | null | undefined): boolean {
  return workObjectDisplaySpec(objectTypeInput).targetRequired;
}
